import { useCallback, useMemo } from 'react'
import type { FC, ReactNode } from 'react'
import { ProvidersUserPeopleRetrieveResponse } from '@fireflyhealth/api'
import { PatientUser } from '~/shared/types'
import { Box, Typography } from '@mui/material'

import EditPersonForm from './EditPersonForm'
import {
  PatientInsuranceAddressPayload,
  useCreateOrUpdateInsuranceAddress,
} from './PersonInsuranceService'
import { useUpdatePerson } from '~/entities/person'
import EditAddressForm from './EditAddressForm'
import EditInsuranceForm from './EditInsuranceForm'
import { SectionCard } from '~/shared/ui'
import { logger } from '~/shared/lib/logger'
interface EditPersonProps {
  patient: ProvidersUserPeopleRetrieveResponse
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
    {children}
  </Typography>
)

/**
 * Design-refresh Profile tab, person mode: the legacy EditPerson's handlers
 * and section forms verbatim, each section framed in a SectionCard.
 */
const EditPerson: FC<EditPersonProps> = props => {
  const { isPending: isUpdatingPerson, mutateAsync: handleUpdatePerson } = useUpdatePerson()
  const {
    isPending: isUpdatingInsuranceAddress,
    mutateAsync: handleCreateOrUpdateInsuranceAddress,
  } = useCreateOrUpdateInsuranceAddress()

  // `EditAddressForm` accepts a `PatientUser`, but the person-only flow has no
  // parent `User` row. Wrap the snake_case person in a stub `PatientUser` shell
  // so the shared form continues to hydrate. Memoize so
  // `enableReinitialize: true` doesn't wipe in-progress edits on every render.
  const patientUser = useMemo(() => ({ person: props.patient }) as PatientUser, [props.patient])

  const handlePersonDetailsSubmit = useCallback(
    // `phone_number` and `request_auth_for_phi` are intentionally omitted from
    // the PATCH: both are read-only on `PersonCRUDSerializer` — `phone_number`
    // is sourced from Auth0 MFA via the phone sync paths, and
    // `request_auth_for_phi` is the PHI access guard and not editable from CRM.
    async (values: ProvidersUserPeopleRetrieveResponse) => {
      const {
        id,
        first_name,
        last_name,
        dob,
        preferred_name,
        employer,
        gender,
        sex,
        pronouns,
        email,
      } = values
      try {
        await handleUpdatePerson({
          id,
          first_name,
          last_name,
          dob,
          preferred_name,
          employer,
          gender,
          sex,
          pronouns,
          email,
        })
      } catch (e) {
        logger.error('[Person] save person details', e)
      }
    },
    [handleUpdatePerson],
  )

  const handleAddressSubmit = useCallback(
    async (values: PatientUser) => {
      const insuranceInfo = values.person.insurance_info
      // The backend only exposes PATCH /insurance/member/{id}; without an
      // existing member id there is no row to update.
      if (insuranceInfo?.id == null) return

      try {
        // PATCH the full first-address row: the nested address serializer (and
        // the generated body schema) require street_address/city/state/zipcode,
        // so the complete row is sent rather than a diffed subset; the backend
        // merges per-field, so re-sending unchanged fields is a no-op.
        const payload: PatientInsuranceAddressPayload = {
          insuranceInfo: {
            id: insuranceInfo.id,
            addresses: insuranceInfo.addresses?.slice(0, 1),
          },
        }
        await handleCreateOrUpdateInsuranceAddress(payload)
      } catch (e) {
        logger.error('[Person] save address', e)
      }
    },
    [handleCreateOrUpdateInsuranceAddress],
  )

  const isLoading = isUpdatingPerson || isUpdatingInsuranceAddress
  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard>
        <SectionTitle>Basic Info</SectionTitle>
        <EditPersonForm
          patient={props.patient}
          handleSubmit={handlePersonDetailsSubmit}
          loading={isLoading}
        />
      </SectionCard>

      <SectionCard>
        <SectionTitle>Address</SectionTitle>
        <EditAddressForm
          handleSubmit={handleAddressSubmit}
          loading={isLoading}
          patient={patientUser}
        />
      </SectionCard>

      {/* Insurance section is self-contained: EditInsuranceForm owns its
          loader (GET /bff/crm/person/{id}/insurance/) and action
          (POST /bff/crm/person/{id}/insurance/). No `userId` is passed,
          which disables the eligibility check button and Redux refresh — both
          require a parent User row that doesn't exist in person-only flows.
          It titles itself, so the card adds no header. */}
      <SectionCard>
        <EditInsuranceForm person={props.patient} />
      </SectionCard>
    </Box>
  )
}

export default EditPerson
